import { Check, ChevronDown, Copy, Search, Send, Users } from '@/components/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { INVITE_EXPIRY_OPTIONS, type InviteDTO, type PublicUser } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { conversationApi, messageApi, serverApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Skeleton, Spinner } from '@/components/ui/Spinner';
import { useSubmit } from '@/hooks/useForm';
import { cn, copyText, randomId } from '@/lib/utils';
import { useServerMembers, useServers } from '@/stores/servers';
import { useConversations, useFriends } from '@/stores/social';
import { toast } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';

const EXPIRY_LABELS: Record<number, string> = { 1800: '30 minutes', 3600: '1 hour', 21600: '6 hours', 43200: '12 hours', 86400: '1 day', 604800: '7 days' };

type SendState = 'sending' | 'sent' | 'error';

export function inviteUrl(code: string) {
  return `${location.origin}/invite/${code}`;
}

/**
 * Invite people to a server: one-click invites to friends (sent as a DM that
 * renders as a joinable card), or a shareable link.
 */
export function InviteModal({ serverId, onClose }: { serverId: string; onClose: () => void }) {
  const server = useServers((s) => s.servers[serverId]);
  const [invite, setInvite] = useState<InviteDTO | null>(null);
  const [expires, setExpires] = useState<string>('604800');
  const [maxUses, setMaxUses] = useState<string>('');
  const [showOptions, setShowOptions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState('');
  const [sent, setSent] = useState<Record<string, SendState>>({});
  const { loading, error, run } = useSubmit();

  const friendsStatus = useFriends((s) => s.status);
  const friends = useFriends((s) => s.friends);
  const conversations = useConversations((s) => s.conversations);
  const users = useUsers((s) => s.users);
  const presences = usePresence((s) => s.statuses);
  const members = useServerMembers(serverId);
  const membersLoaded = useServers((s) => s.membersState[serverId] === 'loaded');

  const generate = useCallback(async () => {
    const created = await run(() =>
      serverApi.createInvite(serverId, { expiresInSeconds: expires === 'never' ? null : Number(expires), maxUses: maxUses ? Number(maxUses) : null }),
    );
    if (created) setInvite(created);
  }, [serverId, expires, maxUses, run]);

  useEffect(() => {
    void generate();
    void useFriends.getState().load();
    void useServers.getState().loadMembers(serverId);
  }, []);

  // Friends first, plus anyone you already have a DM with.
  const candidates = useMemo(() => {
    const byId = new Map<string, PublicUser>();
    for (const f of Object.values(friends)) byId.set(f.user.id, users[f.user.id] ?? f.user);
    for (const c of Object.values(conversations)) {
      const recipient = c.type === 'DM' ? c.recipients[0] : undefined;
      if (recipient && !byId.has(recipient.id)) byId.set(recipient.id, users[recipient.id] ?? recipient);
    }
    const q = query.trim().toLowerCase();
    const rank = (id: string) => ((presences[id] ?? 'offline') === 'offline' ? 1 : 0);
    return [...byId.values()]
      .filter((u) => !q || u.displayName.toLowerCase().includes(q) || u.username.includes(q))
      .sort((a, b) => rank(a.id) - rank(b.id) || a.displayName.localeCompare(b.displayName));
  }, [friends, conversations, users, presences, query]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.userId)), [members]);
  const link = invite ? inviteUrl(invite.code) : '';

  const sendTo = async (user: PublicUser) => {
    if (!invite) return;
    setSent((s) => ({ ...s, [user.id]: 'sending' }));
    try {
      const conversation = await conversationApi.open(user.id);
      useConversations.getState().upsert(conversation);
      await messageApi.send({ conversationId: conversation.id }, { content: link, nonce: randomId() });
      setSent((s) => ({ ...s, [user.id]: 'sent' }));
    } catch (err) {
      setSent((s) => ({ ...s, [user.id]: 'error' }));
      toast.error(`Couldn't invite ${user.displayName}: ${errorMessage(err)}`);
    }
  };

  const copy = async () => {
    if (await copyText(link)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } else toast.error('Could not access the clipboard.');
  };

  return (
    <Modal open onClose={onClose} title={`Invite friends to ${server?.name ?? 'this server'}`}>
      <label className="relative block">
        <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for friends"
          aria-label="Search for friends"
          data-autofocus
          className="h-10 w-full rounded-xl border border-line bg-sunken pr-3 pl-9 text-sm text-fg placeholder:text-fg-muted focus:border-accent focus:outline-none"
        />
      </label>

      <ul className="-mx-2 mt-3 h-[min(300px,40dvh)] overflow-y-auto" aria-label="Friends">
        {friendsStatus !== 'loaded' ? (
          Array.from({ length: 5 }, (_, i) => (
            <li key={i} className="flex items-center gap-3 px-2 py-2">
              <Skeleton className="h-9 w-9 rounded-full" />
              <Skeleton className="h-3.5 flex-1" />
            </li>
          ))
        ) : candidates.length === 0 ? (
          <li className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-fg-muted">
            <Users size={28} />
            {query ? `No friends match “${query}”.` : 'Add some friends and they’ll show up here for one-click invites.'}
          </li>
        ) : (
          candidates.map((user) => {
            const state = sent[user.id];
            const isMember = membersLoaded && memberIds.has(user.id);
            return (
              <li key={user.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-hover/60">
                <Avatar user={user} size={36} status={presences[user.id] ?? 'offline'} ring="ring-elevated" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-fg">{user.displayName}</span>
                  <span className="block truncate text-xs text-fg-muted">@{user.username}</span>
                </span>
                {isMember ? (
                  <span className="px-3 text-xs font-semibold text-fg-muted">Already a member</span>
                ) : (
                  <Button
                    size="sm"
                    variant={state === 'sent' ? 'success' : 'secondary'}
                    disabled={!invite || state === 'sending' || state === 'sent'}
                    onClick={() => void sendTo(user)}
                    icon={state === 'sending' ? <Spinner size={14} /> : state === 'sent' ? <Check size={15} /> : <Send size={14} />}
                    className="min-w-24"
                    aria-label={state === 'sent' ? `Invite sent to ${user.displayName}` : `Invite ${user.displayName}`}
                  >
                    {state === 'sent' ? 'Sent' : state === 'error' ? 'Retry' : 'Invite'}
                  </Button>
                )}
              </li>
            );
          })
        )}
      </ul>

      <div className="mt-4 border-t border-line pt-4">
        <p className="mb-2 text-xs font-bold tracking-wide text-fg-soft uppercase">Or, send a server invite link</p>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-sunken p-1.5 pl-3">
          {loading && !invite ? <Spinner size={16} /> : <span className="min-w-0 flex-1 truncate font-mono text-sm">{link || '—'}</span>}
          <Button size="sm" variant={copied ? 'success' : 'primary'} icon={copied ? <Check size={15} /> : <Copy size={15} />} onClick={() => void copy()} disabled={!invite}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <button
          type="button"
          onClick={() => setShowOptions((v) => !v)}
          aria-expanded={showOptions}
          className="mt-2 flex items-center gap-1 text-xs text-fg-muted hover:text-fg-soft"
        >
          {invite?.expiresAt ? `Your invite link expires ${new Date(invite.expiresAt).toLocaleDateString()}` : 'Your invite link never expires'}
          {invite?.maxUses ? ` · ${invite.maxUses} use${invite.maxUses === 1 ? '' : 's'}` : ''}. <span className="text-link">Edit invite link</span>
          <ChevronDown size={14} className={cn('transition-transform', showOptions && 'rotate-180')} />
        </button>
        {showOptions && (
          <div className="mt-3 animate-fade-in">
            <div className="grid grid-cols-2 gap-3">
              <Select label="Expire after" value={expires} onChange={(e) => setExpires(e.target.value)}>
                {INVITE_EXPIRY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {EXPIRY_LABELS[s]}
                  </option>
                ))}
                <option value="never">Never</option>
              </Select>
              <Select label="Max uses" value={maxUses} onChange={(e) => setMaxUses(e.target.value)}>
                <option value="">No limit</option>
                {[1, 5, 10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} use{n === 1 ? '' : 's'}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void generate()} loading={loading && !!invite}>
              Generate new link
            </Button>
          </div>
        )}
        {error && (
          <p className="mt-3 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
